import React from 'react';
import { Users, MapPin, CheckCircle2, BadgeCheck, ShieldCheck, Lock, MailCheck, Award, BookOpen, TrendingUp } from 'lucide-react';

/**
 * Mapa de Portugal — versão clara para uso como marca-d'água decorativa
 * no hero ou em outras áreas com fundo branco/cinza claro.
 */
export function PortugalNeonMap({ className = '', width = 240, height = 320 }) {
  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox="0 0 280 380"
      fill="none"
      aria-hidden="true"
      data-testid="portugal-neon-map"
    >
      <defs>
        <linearGradient id="pt-fill-light" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <path
        d="M 90 30 L 110 22 L 130 28 L 145 40 L 140 65 L 155 90 L 145 115 L 158 140 L 152 175 L 165 200 L 158 235 L 145 270 L 135 305 L 115 340 L 95 360 L 70 355 L 60 335 L 65 305 L 55 275 L 60 245 L 55 215 L 60 180 L 55 145 L 65 110 L 60 78 L 75 50 Z"
        stroke="#10b981"
        strokeOpacity="0.55"
        strokeWidth="2"
        fill="url(#pt-fill-light)"
      />
      {/* Cidades — pinos pequenos verdes */}
      <g transform="translate(80, 240)">
        <circle r="9" fill="#10b981" opacity="0.18" />
        <circle r="4" fill="#10b981" />
      </g>
      <g transform="translate(85, 115)">
        <circle r="7" fill="#10b981" opacity="0.18" />
        <circle r="3" fill="#10b981" />
      </g>
      <g transform="translate(105, 340)">
        <circle r="6" fill="#10b981" opacity="0.18" />
        <circle r="3" fill="#10b981" />
      </g>
    </svg>
  );
}

/**
 * Hero Premium — uma única composição: texto à esquerda e zona visual à direita
 * (mapa de Portugal + linhas verdes + TVDE / Uber / Bolt + card flutuante).
 * O fundo do hero usa o mesmo tom do asset (#fcfcfc) para a imagem se integrar
 * sem parecer um retângulo colado por cima do site.
 */
export function PremiumHero({ children }) {
  return (
    <section
      className="relative isolate overflow-hidden border-b border-slate-200"
      style={{ backgroundColor: '#fcfcfc' }}
      data-testid="marketplace-premium-hero"
    >
      <div className="mx-auto w-[calc(100%-32px)] max-w-[1680px] py-8 md:w-[calc(100%-64px)] md:py-9 lg:py-10">
        {children}
      </div>
    </section>
  );
}

/**
 * Zona visual do hero. O mapa é um elemento decorativo absoluto (preso a este
 * container, nunca à página) e o card de confiança flutua no canto direito.
 * Abaixo de lg tudo passa a fluxo normal: mapa e depois o card.
 */
export function HeroComposition({ children }) {
  return (
    <div
      className="relative xl:min-h-[540px] 2xl:min-h-[580px]"
      data-testid="marketplace-hero-visual"
    >
      {/* Mapa decorativo — desktop */}
      <img
        src="/marketplace/hero-bg.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-[-45%] top-1/2 z-0 hidden w-[92%] max-w-none -translate-y-1/2 select-none object-contain xl:block 2xl:left-[-38%] 2xl:w-full"
        style={{
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 14%, #000 30%, #000 96%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 13%, #000 88%, transparent 100%)',
          maskImage:
            'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 14%, #000 30%, #000 96%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 13%, #000 88%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          maskComposite: 'intersect',
        }}
        data-testid="marketplace-hero-map-desktop"
      />
      {/* Mapa em fluxo — tablet/mobile */}
      <img
        src="/marketplace/hero-bg.png"
        alt="Marketplace TVDE Portugal — Uber & Bolt"
        className="mx-auto mb-6 block w-full max-w-[640px] select-none xl:hidden"
        loading="eager"
        style={{
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, #000 10%, #000 96%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 8%, #000 94%, transparent 100%)',
          maskImage:
            'linear-gradient(to right, transparent 0%, #000 10%, #000 96%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 8%, #000 94%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          maskComposite: 'intersect',
        }}
        data-testid="marketplace-hero-map-mobile"
      />
      {/* Card flutuante */}
      <div className="relative z-10 xl:absolute xl:right-0 xl:top-[70px] xl:w-[320px]">
        {children}
      </div>
    </div>
  );
}

/** Badges de confiança no hero — versão clara com checks verdes */
export function PlatformBadges({ pagamentosAtivos = false }) {
  const items = [
    'Especializado em TVDE',
    pagamentosAtivos ? 'Pagamentos processados em ambiente seguro' : 'Publicar é gratuito neste momento',
    'Contactos confirmados por email e SMS',
    'Anúncios sujeitos a validação',
  ];
  return (
    <div
      className="mt-6 grid max-w-[600px] grid-cols-1 gap-2.5 sm:grid-cols-2"
      data-testid="marketplace-platform-badges"
    >
      {items.map((label) => (
        <span
          key={label}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm"
        >
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          {label}
        </span>
      ))}
    </div>
  );
}

/**
 * Card "Confiança em primeiro lugar" — cartão branco premium para o lado
 * direito do hero, junto ao mapa de Portugal. Textos deliberadamente
 * prudentes (sem alegações absolutas de segurança/fraude).
 */
export function ConfidenceCard({ pagamentosAtivos = false }) {
  const items = [
    {
      icon: ShieldCheck,
      title: 'Prevenção de fraude',
      desc: 'Aplicamos mecanismos de controlo e revisão para reduzir anúncios suspeitos e aumentar a confiança na plataforma.',
    },
    {
      icon: Lock,
      title: pagamentosAtivos ? 'Pagamentos seguros' : 'Sem custos neste momento',
      desc: pagamentosAtivos
        ? 'Os pagamentos dos serviços do Marketplace TVDE são processados através de parceiros de pagamento especializados.'
        : 'Publicar não tem qualquer custo neste momento. Os métodos de pagamento ficam disponíveis quando terminar o período gratuito.',
    },
    {
      icon: MailCheck,
      title: 'Contactos verificados',
      desc: 'Os contactos dos utilizadores podem ser confirmados por email e SMS para aumentar a qualidade dos anúncios.',
    },
    {
      icon: BadgeCheck,
      title: 'Anúncios revistos',
      desc: 'Os anúncios podem ser sujeitos a revisão para promover maior transparência e segurança.',
    },
  ];
  return (
    <div
      className="animate-fade-in w-full rounded-3xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ring-slate-900/5 md:p-6"
      data-testid="marketplace-confidence-card"
    >
      <h2 className="text-base font-bold tracking-tight text-slate-900">Confiança em primeiro lugar</h2>
      <div className="mt-4 space-y-4">
        {items.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="flex items-start gap-2.5">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
              <Icon className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-slate-900">{title}</p>
              <p className="mt-0.5 text-[11.5px] leading-snug text-slate-500">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Faixa institucional — 4 blocos de posicionamento de marca sobre fundo verde
 * muito claro (nunca preto). 2x2 em mobile, 4 colunas em desktop.
 */
export function InstitutionalStrip() {
  const blocks = [
    {
      icon: Award,
      title: 'Especializado em TVDE',
      desc: 'Uma plataforma criada exclusivamente para o ecossistema TVDE em Portugal.',
    },
    {
      icon: Users,
      title: 'O ponto de encontro do mercado TVDE',
      desc: 'Motoristas, operadores, proprietários, investidores e profissionais reunidos num só lugar.',
    },
    {
      icon: BookOpen,
      title: 'Informação que ajuda',
      desc: 'Guias, novidades e informação útil para acompanhar a evolução do setor TVDE.',
    },
    {
      icon: TrendingUp,
      title: 'O mercado TVDE num só lugar',
      desc: 'Viaturas, motoristas, serviços e oportunidades reunidos numa plataforma especializada.',
    },
  ];
  return (
    <section
      className="border-y border-emerald-100/70 bg-emerald-50/30"
      data-testid="marketplace-institutional-strip"
    >
      <div className="mx-auto w-[calc(100%-32px)] max-w-[1680px] py-8 md:w-[calc(100%-64px)] md:py-10">
        <div className="grid grid-cols-1 gap-y-7 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-emerald-100/80">
          {blocks.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3 px-0 lg:px-7 lg:first:pl-0 lg:last:pr-0">
              <span className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100">
                <Icon className="h-[19px] w-[19px]" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="text-[15px] font-bold leading-tight text-slate-900">{title}</p>
                <p className="mt-1 text-[13.5px] leading-snug text-slate-500">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-7 flex flex-col items-center gap-1 border-t border-emerald-100/80 pt-5 text-center"
          data-testid="marketplace-security-line"
        >
          <p className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-700">
            <Lock className="h-4 w-4 text-emerald-500" />
            A sua segurança é uma prioridade.
          </p>
          <p className="text-[12.5px] text-slate-500">
            Dados protegidos · Plataforma especializada · Contactos confirmados por email e SMS
          </p>
        </div>

        <p
          className="mt-4 text-center text-[11px] text-slate-400"
          data-testid="marketplace-independence-note"
        >
          Marketplace independente, sem afiliação oficial às plataformas apresentadas.
        </p>
      </div>
    </section>
  );
}

/** Pino de localização animado — uso decorativo */
export function NeonPin({ className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <MapPin className="h-6 w-6 text-emerald-500" />
      <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-emerald-500/30" />
    </div>
  );
}

